import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type EmployeeData = {
  name: string;
  phone: string;
  email?: string;
  status: "active" | "inactive";
};

type StepEmployeeProps = {
  onSubmit: (data: EmployeeData) => Promise<void>;
  initialData: EmployeeData | null;
  isLoading: boolean;
};

export const StepEmployee = ({ onSubmit, initialData, isLoading }: StepEmployeeProps) => {
  const [formData, setFormData] = useState<EmployeeData>({
    name: "",
    phone: "",
    email: "",
    status: "active",
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleInputChange = (field: keyof EmployeeData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const isFormValid = () => {
    return formData.name.trim() !== "" && formData.phone.trim() !== "";
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-lg font-semibold mb-4">Paso 4: Crear empleado</p>
        <p className="text-sm text-muted-foreground mb-4">
          Crea un empleado que será asignado al proyecto para manejar las conversaciones
          transferidas.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Ingresa el nombre del empleado"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="phone">Teléfono *</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            placeholder="Ingresa el número de teléfono"
            type="tel"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder="Ingresa el email (opcional)"
            type="email"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="status">Estado *</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => handleInputChange("status", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona el estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="inactive">Inactivo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || isLoading}
        >
          {isLoading ? "Creando..." : "Finalizar y crear agente"}
        </Button>
      </div>
    </div>
  );
};
